import { Button, Checkbox, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import PublishDialogIcon from "@entrilia/shared/icons/PublishDialogIcon";
import PublishToPreviewDialogIcon from "@entrilia/shared/icons/PublishToPreviewDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DocumentCollectionOperations } from "../../../api/types/documentCollectionTypes";

interface Props {
  operationLevel: "documents" | "document_collections";
  selectedIds: string[];
  operation: DocumentCollectionOperations;
  documentsInfo: string;
  onClose: () => void;
  onApply: (jobId: string) => void;
}

const changeDocumentCollectionsStatus = withErrorHandling(adminApi.changeDocumentCollectionsStatus);
const changeDocumentsStatus = withErrorHandling(adminApi.changeDocumentCollectionDocumentsStatus);

const operationTitleMap: Record<DocumentCollectionOperations, string> = {
  MakeLive: "Publish",
  MakePreview: "Unpublish",
  Revoke: "Delete",
};

const getPrompt = (operation: DocumentCollectionOperations, documentsInfo: string) => {
  switch (operation) {
    case "MakeLive": {
      return `Publish ${documentsInfo} to Live`;
    }
    case "MakePreview": {
      return `Unpublish ${documentsInfo} to Preview`;
    }
    case "Revoke": {
      return `Delete ${documentsInfo}?`;
    }
    default: {
      return "";
    }
  }
};

const getSuccessNotificationMessage = (operation: DocumentCollectionOperations, documentsInfo: string) => {
  switch (operation) {
    case "MakeLive": {
      return `Successfully published ${documentsInfo} to Live`;
    }
    case "MakePreview": {
      return `Successfully unpublished ${documentsInfo} to Preview`;
    }
    case "Revoke": {
      return `Successfully deleted ${documentsInfo}`;
    }
    default: {
      return "";
    }
  }
};

const getDescription = (operation: DocumentCollectionOperations, documentsInfo: string) => {
  switch (operation) {
    case "MakeLive": {
      return `You're about to make documents "Live" and available for contacts. This process can take a few minutes.`;
    }
    case "MakePreview": {
      return `The documents will be returned in "Preview" and will not be available for viewing by contacts. This process can take a few minutes.`;
    }
    case "Revoke": {
      return `You're about to delete ${documentsInfo}. This action is permanent and can't be undone. This process can take a few minutes.`;
    }
    default: {
      return "";
    }
  }
};

const StatusIcon = ({ operation }: { operation: DocumentCollectionOperations }) => {
  switch (operation) {
    case "MakeLive": {
      return <PublishDialogIcon sx={{ width: 80, height: 80 }} />;
    }
    case "MakePreview": {
      return <PublishToPreviewDialogIcon sx={{ width: 80, height: 80 }} />;
    }
    case "Revoke": {
      return <DeleteDialogIcon sx={{ width: 80, height: 80 }} />;
    }
    default: {
      return null;
    }
  }
};

const ChangeDocumentCollectionsStatusDialog = ({
  operationLevel,
  documentsInfo,
  selectedIds,
  operation,
  onClose,
  onApply,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);
  const [reportCompletionViaEmail, setReportCompletionViaEmail] = useState(false);

  const handleApply = async () => {
    setIsSaving(true);

    const [resp, error] =
      operationLevel === "document_collections"
        ? await changeDocumentCollectionsStatus({
            documentCollectionIds: selectedIds,
            operation,
            reportCompletionViaEmail,
          })
        : await changeDocumentsStatus({
            postMessageRequestIds: selectedIds,
            operation,
            reportCompletionViaEmail,
          });

    setIsSaving(false);

    if (error) {
      logError(error, "[ChangeDocumentCollectionsStatusDialog] changeDocumentCollectionsStatus");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(getSuccessNotificationMessage(operation, documentsInfo));
    onApply(resp.jobId);
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{operationTitleMap[operation]}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <StatusIcon operation={operation} />

          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">{getPrompt(operation, documentsInfo)}</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {getDescription(operation, documentsInfo)}
            </Typography>
          </Stack>

          <FormControlLabel
            label="Send me an email when the process is finished"
            control={
              <Checkbox
                checked={reportCompletionViaEmail}
                onChange={(_, checked) => setReportCompletionViaEmail(checked)}
              />
            }
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant={operation === "Revoke" ? "outlined" : "contained"}
          color={operation === "Revoke" ? "error" : "primary"}
          loading={isSaving}
          onClick={handleApply}
        >
          {operationTitleMap[operation]}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ChangeDocumentCollectionsStatusDialog;
